// src/context/brandVoice/widgets/ClaimTable.jsx — the rows of ONE claims category (v200), drawn on the grid its
// area shares (ClaimsEditor owns the area and its title).
//
// ⭐ `columns` are the AREA's columns, `keys` this category's own: a column the category lacks is an empty cell, so
// every row of the area lines up. There are no header rows: each placeholder names its column, then a per-category
// EXAMPLE (`brandVoice.claimPlaceholder.<category>.<key>`, falling back to the shared `recordField` one) —
// "Etichetta*: es. …", "Fonte (opz.): es. …". Every cell is a growing one-line box that also grows to fit its
// placeholder while empty (GrowingTextarea `fitPlaceholder`); the first one is bold.
// ⭐ An EMPTY category shows ONE blank row to type in — a view only: no remove button, nothing handed up until the user
// types (`wireShape.toWire` drops a row left blank). ⭐ The add button is an icon on the LAST row (no line of its own).
// Both buttons show only on a row holding text in some cell — not on a blank row, nor on one just added. The glyph is
// a PLACEHOLDER (ArrowDown: "a row below"): the designers pick the final one; the position is the decision.
// ⭐ It edits ROWS and hands up a WHOLE new array; an edit SPREADS the row, so the passthrough `note` and the never
// shown `as_of` survive. ⛔ It validates nothing: `problems` only marks a row `aria-invalid`.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, X } from 'lucide-react';
import GrowingTextarea, { blockEnter, oneLine } from '../ui/GrowingTextarea.jsx';
import { ADD_ICON, INPUT, REMOVE } from './listStyles.js';
import { COLORS } from '../../../constants/theme.js';

const str = (v) => (typeof v === 'string' ? v : '');
const CELL = (invalid) => ({ ...INPUT(invalid), width: '100%' });
// The category name: the same weight the area titles had (v200).
const SUBLABEL = { fontSize: 14, fontWeight: 700, color: COLORS.ink };

export default function ClaimTable({
  category, label, showLabel = true, columns, keys, required = [], grid, value, onChange, disabled = false,
  problems = [],
}) {
  const { t } = useTranslation('context');
  const stored = Array.isArray(value) ? value : [];
  const virtualRow = stored.length === 0;
  const rows = virtualRow ? [{}] : stored;
  const badRows = new Set((problems || []).map((p) => p?.params?.row).filter(Boolean));
  const nameOf = (key) => t(`brandVoice.recordField.${key}.name`, { defaultValue: key });
  const exampleOf = (key) => t(`brandVoice.claimPlaceholder.${category}.${key}`, {
    defaultValue: t(`brandVoice.recordField.${key}.placeholder`, { defaultValue: key }),
  });
  const placeholderOf = (key) => (
    `${nameOf(key)}${required.includes(key) ? '*' : ` ${t('brandVoice.claims.optionalMark')}`}: ${exampleOf(key)}`);
  const blank = () => Object.fromEntries(keys.map((k) => [k, '']));
  const replace = (index, key, text) => onChange?.(rows.map((r, i) => (
    i === index ? { ...(r && typeof r === 'object' ? r : {}), [key]: text } : r
  )));
  const remove = (index) => onChange?.(stored.filter((_, i) => i !== index));
  const add = () => onChange?.([...stored, blank()]);

  return (
    <div role="group" aria-label={label} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {showLabel && <span style={SUBLABEL}>{label}</span>}
      {rows.map((row, i) => {
        const n = i + 1;
        const invalid = badRows.has(n);
        const cellLabel = (key) => t('brandVoice.record.cellLabel', { field: nameOf(key), row: n });
        const last = i === rows.length - 1;
        // A row with no text in any shown cell (the blank row of an empty category, or one just added) offers neither
        // remove nor add: it is dropped on save, and "add" under it would only stack blank rows.
        const filled = keys.some((key) => str(row?.[key]).trim() !== '');
        return (
          // No reordering, so the index is a stable key for the life of a row.
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} style={{ display: 'grid', gridTemplateColumns: grid, gap: 10, alignItems: 'start' }}>
            {columns.map((key, k) => {
              if (!keys.includes(key)) return <span key={key} />;
              const common = {
                'aria-label': cellLabel(key),
                'aria-invalid': invalid || undefined,
                placeholder: placeholderOf(key),
                value: str(row?.[key]),
                disabled,
                onChange: (e) => replace(i, key, oneLine(e.target.value)),
              };
              return (
                <GrowingTextarea
                  key={key}
                  {...common}
                  fitPlaceholder
                  onKeyDown={blockEnter}
                  style={{ ...CELL(invalid), ...(k === 0 ? { fontWeight: 600 } : null) }}
                />
              );
            })}
            {!filled ? <span /> : (
              <button
                type="button"
                aria-label={t('brandVoice.record.remove', { row: n })}
                disabled={disabled}
                onClick={() => remove(i)}
                style={REMOVE(disabled)}
              >
                <X size={16} strokeWidth={1.8} aria-hidden="true" />
              </button>
            )}
            {last && filled ? (
              <button
                type="button"
                aria-label={t('brandVoice.record.addRow')}
                title={t('brandVoice.record.addRow')}
                disabled={disabled}
                onClick={add}
                style={ADD_ICON(disabled)}
              >
                <ArrowDown size={22} strokeWidth={2} aria-hidden="true" />
              </button>
            ) : <span />}
          </div>
        );
      })}
    </div>
  );
}
